import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Crown, Medal, TrendingUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLE_STYLE = {
  affiliate: "bg-pink-500/20 text-pink-300 border-pink-500/30",
  customer: "bg-slate-500/20 text-slate-300 border-slate-500/30",
};

const RANK_ICON = [Crown, Trophy, Medal];
const RANK_COLOR = ["text-amber-300", "text-slate-200", "text-orange-400"];

export default function Leaderboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("week");

  useEffect(() => {
    api.get(`/admin/analytics/leaderboard?period=${period}&limit=10`).then((r) => setData(r.data));
  }, [period]);

  if (!data) return <div className="velo-card p-6 h-56 animate-pulse" />;

  return (
    <div className="velo-card p-5 sm:p-6">
      <div className="flex items-start sm:items-center justify-between mb-5 flex-col sm:flex-row gap-3">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-pink-400" strokeWidth={2} />
            <h3 className="text-lg text-white font-medium">Top referrers</h3>
          </div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider mt-1">
            Ranked by points earned · {data.total_closed} referrals closed · {data.total_referrers} referrers
          </p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger data-testid="select-leaderboard-period" className="velo-input h-10 w-40"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#22103F] border-white/10 text-white">
            <SelectItem value="week">This week</SelectItem>
            <SelectItem value="month">Last 30 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        {data.leaders.map((r, i) => {
          const Icon = RANK_ICON[i];
          const color = RANK_COLOR[i] || "text-slate-500";
          const roleCls = ROLE_STYLE[r.referrer_role] || ROLE_STYLE.customer;
          return (
            <div key={r.referrer_id} data-testid={`leaderboard-row-${i}`} className="flex items-center gap-4 p-3 sm:p-4 velo-surface rounded-lg">
              <div className="flex items-center justify-center w-9 h-9 shrink-0">
                {Icon ? <Icon className={`w-5 h-5 ${color}`} strokeWidth={2} /> : <span className="text-sm text-slate-500 font-mono">#{i + 1}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-white font-medium truncate">{r.referrer_name}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${roleCls}`}>{r.referrer_role}</span>
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5">
                  <span className="font-mono">{r.referrer_phone}</span>
                  {r.affiliate_type ? <span className="ml-2">· {r.affiliate_type}</span> : null}
                  {r.associate_firm ? <span className="ml-1 text-slate-400">· {r.associate_firm}</span> : null}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-sm text-pink-300">+{r.points_earned.toLocaleString("en-IN")} pts</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{r.closed_count} closed</div>
              </div>
            </div>
          );
        })}
        {data.leaders.length === 0 ? (
          <div className="text-slate-500 text-center py-8 text-sm">No closed referrals in this window yet.</div>
        ) : null}
      </div>
    </div>
  );
}
